import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StarRating from './StarRating';
import type { HuntingOutlook, PeriodForecast, IndicatorStatus } from '../pages/DashboardPage';
import { locationClock, selectHomeWindows, HuntWindow } from '../services/homeWindows';
import { formatHomeDate } from '../services/forecastDates';
import { formatForecastTime } from '../services/forecastTime';
import './HomeView.css';

function words(value: string): string {
  return value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function WeatherBubbles({ period }: { period: PeriodForecast }) {
  const c = period.conditions;
  const indicators = period.indicators;
  const bubbles: { key: string; label: string; value: string; status?: IndicatorStatus }[] = [
    { key: 'temperature', label: 'Avg temp', value: `${c.tempF}\u00b0F`, status: indicators?.temperature },
    { key: 'wind', label: 'Avg wind', value: `${c.windSpeedMph} mph`, status: indicators?.wind },
    { key: 'gusts', label: 'Gusts up to', value: `${c.windGustMph} mph`, status: indicators?.gusts },
    { key: 'clouds', label: 'Clouds', value: `${c.cloudPercent}%`, status: indicators?.clouds },
    { key: 'pressure', label: 'Pressure', value: c.pressureTrend === 'stable' ? 'Steady' : words(c.pressureTrend), status: indicators?.pressure },
    { key: 'precipitation', label: 'Precip', value: c.precipitationType === 'none' ? 'Dry' : words(c.precipitationType), status: indicators?.precipitation },
    ...(c.frontType === 'none' ? [] : [{ key: 'front', label: 'Front', value: words(c.frontType), status: indicators?.front }]),
  ];
  return (
    <dl className="home-conditions">
      {bubbles.map(bubble => (
        <div key={bubble.key} className={bubble.status === 'good' || bubble.status === 'bad' ? `home-condition-${bubble.status}` : undefined}>
          <dt>{bubble.label}</dt><dd>{bubble.value}</dd>
          {(bubble.status === 'good' || bubble.status === 'bad') && (
            <span className="sr-only">{bubble.status === 'good' ? 'Favorable indicator' : 'Caution indicator'}</span>
          )}
        </div>
      ))}
    </dl>
  );
}

function HomeView({ outlook, header, onRefresh }: {
  outlook: HuntingOutlook; header: React.ReactNode; onRefresh: () => void;
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setNow(new Date());
      timer = setTimeout(tick, 60000 - Date.now() % 60000);
    };
    timer = setTimeout(tick, 60000 - Date.now() % 60000);
    const resume = () => { if (document.visibilityState === 'visible') setNow(new Date()); };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, []);
  let windows: HuntWindow[] = [];
  let currentDate = '';
  let error = '';
  try {
    currentDate = locationClock(outlook, now).date;
    windows = selectHomeWindows(outlook, now);
  } catch (err) {
    error = err instanceof Error ? err.message : 'Unable to select hunting windows';
  }
  const previousDate = useRef(currentDate);
  useEffect(() => {
    if (error) console.error('Home hunting windows unavailable:', error);
  }, [error]);
  useEffect(() => {
    if (previousDate.current && currentDate && currentDate !== previousDate.current) onRefresh();
    previousDate.current = currentDate;
  }, [currentDate, onRefresh]);
  const labels: Record<number, string> = { 1: 'Poor', 2: 'Below Average', 3: 'Average', 4: 'Good', 5: 'Excellent' };
  if (error) {
    return <div className="home-outlook">{header}<div className="error-state" role="alert">
      <p>Unable to load the current and next hunting windows. Please try again.</p>
      <button className="btn btn-primary" onClick={onRefresh}>Try Again</button>
    </div></div>;
  }
  return (
    <div className="home-outlook">
      {header}
      <div className="home-hunts">
        {windows.map(({ forecast, name }) => {
          const period = forecast[name];
          const label = name === 'morning' ? 'Morning' : 'Evening';
          return (
            <section className="home-hunt" key={`${forecast.date}-${name}`} aria-labelledby={`home-${name}`}>
              <p className="home-date"><time dateTime={forecast.date}>{formatHomeDate(forecast.date)}</time></p>
              <div className="home-period-header">
                <div>
                  <h3 id={`home-${name}`}>{label} Hunt</h3>
                  <p>{name === 'morning' ? `Sunrise ${formatForecastTime(forecast.sunrise)}` : `Sunset ${formatForecastTime(forecast.sunset)}`}</p>
                </div>
                <div className="home-rating">
                  <StarRating rating={period.rating} />
                  <span>{labels[period.rating]}</span>
                </div>
              </div>
              <p className="home-lead">{period.briefing?.opening || forecast[`${name}Summary`]}</p>
              {period.briefing && (
                <details className="home-details" key={`${forecast.date}-${name}`}>
                  <summary aria-label={`${label} hunt details`}>
                    <span className="home-more">Read more</span><span className="home-less">Read less</span>
                  </summary>
                  <div className="home-briefing">
                    {period.briefing.explanation.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                    <p><strong>Practical takeaway: </strong>{period.briefing.takeaway}</p>
                  </div>
                </details>
              )}
              <WeatherBubbles period={period} />
              <Link className="btn home-action" to={`/daily?date=${forecast.date}`} aria-label={`${label} hunt hourly details for ${formatHomeDate(forecast.date)}`}>Hourly Details</Link>
            </section>
          );
        })}
      </div>
      <div className="home-actions">
        <Link className="btn home-action" to="/outlook">View 7-Day Outlook</Link>
      </div>
      <p className="home-footnote">Conditions describe the modeled hunt periods, not legal shooting hours. Follow local regulations.</p>
    </div>
  );
}

export default HomeView;
